<template>
  <b-container fluid>
    <backlink />
    <h1>Références uniques</h1>

    <AdminTable :config="config" :options="options" :items_provider="itemsProvider">
      <!-- QR-005 : impression groupée des étiquettes QR code -->
      <template slot="bulk-actions" slot-scope="{ selectedRows }">
        <b-button
          variant="outline-primary"
          class="mr-10"
          @click="openLabelsModal(selectedRows)"
        >Imprimer les étiquettes</b-button>
      </template>
      <template slot="status" slot-scope="{ data }">
        <b-badge variant="primary">{{ data.value }}</b-badge>
      </template>
      <template slot="group_slug" slot-scope="{ data }">
        <div style="width: 75px">
          <b-badge v-if="data.value.includes('outillage')" variant="outillage" class="circle float-left" v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('outillage').text">
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge v-if="data.value.includes('instruments')" variant="instruments" class="circle float-left"
            v-b-tooltip.hover :title="$store.getters['renting_preparation/getGroup']('instruments').text">
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge v-if="data.value.includes('service_et_logistique')" variant="service_et_logistique"
            class="circle float-left" v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('service_et_logistique').text">
            <i class="fa fa-check"></i>
          </b-badge>
        </div>
      </template>
    </AdminTable>

    <!-- QR-005 -->
    <ReferenceLabelsModal
      :selected-rows="labelsSelectedRows"
      :filters="lastQueryFilters"
      :filtered-total="lastQueryTotal"
    />
  </b-container>
</template>

<script>
import AdminTable from "@/components/Global/AdminTableSync";
import ReferenceLabelsModal from "./ReferenceLabelsModal";
import config from "./config";

export default {
  name: "ReferenceList",
  components: {
    AdminTable,
    ReferenceLabelsModal,
  },
  data() {
    return {
      // QR-005 : la liste est paginée côté serveur. La sélection ne porte donc que sur
      // la page affichée ; les filtres courants permettent d'étendre l'impression à
      // tout le résultat de la recherche sans avoir à charger les lignes.
      labelsSelectedRows: [],
      lastQueryFilters: {},
      lastQueryTotal: 0,
    };
  },
  computed: {
    // INC-001 : true si l'utilisateur n'a pas d'entrepôt rattaché (accès à tous les entrepôts)
    isAllWarehouses: function () {
      return !this.$store.getters["auth/warehouse"];
    },
    // INC-001 : on retire le filtre entrepôt pour les utilisateurs rattachés à un
    // entrepôt (périmètre déjà forcé côté serveur)
    config: function () {
      if (this.isAllWarehouses) {
        return config;
      }
      return { ...config, fields: config.fields.filter((field) => field.name !== "warehouse") };
    },
    options: function () {
      let status_options = this.$store.getters["status/getByGroup"]("reference").selectOptions(
        "status_slug",
        "name"
      );
      return {

        status_options: [
          status_options[2],
          status_options[4],
          status_options[1],
          status_options[5],
          status_options[3],
          status_options[9],
          status_options[0],
          status_options[6],
          status_options[8],
          status_options[7],
          status_options[10],
        ],
        group_slug_options: this.$store.getters["renting_preparation/getGroups"],
        equipment_group_options: this.$store.getters["equipment_group/getAllForSelect"],
        // INC-003 : options du filtre entrepôt ("-- Tout --" ajouté par AdminTableSync)
        // « Sans entrepôt » : références dont l'entrepôt courant n'est pas renseigné.
        warehouse_options: [
          { value: "none", text: "Sans entrepôt" },
          ...(this.$store.getters["warehouse/getAll"] || []).map((w) => ({ value: w.id, text: w.internal_code + " - " + w.name })),
        ],
        from_mcs_options: [
          { value: null, text: "Toutes" },
          { value: '1', text: "Import MCS" },
          { value: '0', text: "Catalogue ESM" },
        ],
      };
    },
  },
  mounted() {
    this.$store.dispatch("equipment_group/getAll");
    // INC-001 : entrepôts pour le filtre (seulement utile en accès-tout)
    if (this.isAllWarehouses) {
      this.$store.dispatch("warehouse/getAll");
    }
  },
  methods: {
    /**
     * Fourni les données à AdminTableSync. Cette méthode est appelée à chaque
     * rafraichissement de la liste
     * @param   {object}  ctx   Objet context envoyé par b-table
     * @returns {object}        {
                                  total: Nombre total de résultats,
                                  items: Items à afficher
                                }
     */
    // QR-005 : ouvre la modale d'impression avec la sélection courante.
    openLabelsModal(selectedRows) {
      this.labelsSelectedRows = selectedRows || [];
      this.$root.$emit("bv::show::modal", "modal-reference-labels");
    },
    itemsProvider(ctx) {
      var self = this;

      // QR-005 : mêmes critères que la liste, mémorisés pour permettre d'imprimer
      // tout le périmètre du filtre (le back les rejoue, sans pagination).
      const filters = {
        search: ctx.filter,
        statusSlug: ctx.extended_filters_model.status,
        group_slug: ctx.extended_filters_model.group_slug,
        equipment_group: ctx.extended_filters_model.equipment_group,
        warehouse: ctx.extended_filters_model.warehouse,
      };
      this.lastQueryFilters = filters;

      return new Promise(function (resolve, reject) {
        self.$http
          .get(process.env.BACK_API_URL + "reference", {
            params: {
              page: ctx.currentPage,
              limit: ctx.perPage,
              sortBy: ctx.sortBy,
              sortDesc: ctx.sortDesc ? "DESC" : "ASC",
              // INC-003 : filtre entrepôt de la page (ignoré côté serveur si l'user est déjà scopé)
              ...filters,
              from_mcs: ctx.extended_filters_model.from_mcs,
            },
          })
          .then((response) => {
            self.lastQueryTotal = response.data.total || 0;
            resolve({
              total: response.data.total,
              page: parseInt(response.data.page),
              items: response.data.references || [],
            });
          })
          .catch((error) => {
            self.$toastr.error("Le chargement de la liste a échoué");
            self.inGenerating = false;
          });
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Reference/ReferenceList.vue